import type { QueryClient } from '@tanstack/react-query'
import type { TFunction } from 'i18next'

/**
 * Shared bits of the parent/child tickets, follow-ups and notices UI (spec
 * 2026-10-27-19).
 */

/** A wait_for proof spec: `{kind, ...}` (catalogue of spec 2026-10-28-15 § 6). */
export type WaitFor = { kind?: string } & Record<string, unknown>

/** The human label of what a step waits for. */
export function proofLabel(t: TFunction, waitFor: unknown): string {
    const kind = (waitFor as WaitFor | null | undefined)?.kind
    if (!kind) return ''
    return t(`proof.${kind}`, { defaultValue: kind })
}

/** Whether the ticket closes on a proof it has not met yet. */
export function awaitsProof(issue: { wait_for?: unknown; proof?: unknown }): boolean {
    return Boolean((issue.wait_for as WaitFor | null | undefined)?.kind) && !issue.proof
}

/** A follow-up is due once its date has passed. */
export function followUpDue(followUpAt: string | null | undefined, now = Date.now()): boolean {
    return Boolean(followUpAt) && new Date(followUpAt as string).getTime() <= now
}

/** Refetch every issue list, detail and count after a family change. */
export function invalidateIssueQueries(queryClient: QueryClient) {
    queryClient.invalidateQueries({
        predicate: (query) => {
            const key = query.queryKey as unknown[]
            return key.length >= 2 && key[0] === 'get' && typeof key[1] === 'string' &&
                (key[1] as string).startsWith('/api/v1/organizations/{orgCode}/issues')
        },
    })
}

/** The API error code of a failed call, when it carries one. */
export function errorCode(error: unknown): string | undefined {
    const code = (error as { code?: unknown } | null | undefined)?.code
    return typeof code === 'string' ? code : undefined
}
